import * as React from 'react';
import * as TypeStyle from 'typestyle';

import { InteractiveDomWrapper } from '../../utils/InteractiveDom';
import { SliderHandleIcon, SliderLeftHandleIcon, SliderRightHandleIcon } from '../../icons/Icons';
import { makeTestId } from '../../utils/Declarative';
import { addClientRectLeft, addClientRectRight } from '../../utils/Geometry';

import { getSliderHandleStyles } from './SliderStyles';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';

const FocusIndicatorStyleName = TypeStyle.style({ // HACK THE PLANET
  $nest: {
    '&.focus-within::after': {
      content: `""`,
      pointerEvents: 'none',
      width: 16,
      height: 16,
      display: 'inline-block',
      borderRadius: '50%',
      top: 12,
      left: -8,
      position: 'absolute',
      boxShadow: DefaultFocusIndicator.boxShadow,
    },
    '&:not(:only-child).focus-within::after': {
      borderTopLeftRadius: '66.66% 50%',
      borderBottomLeftRadius: '66.66% 50%',
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      left: -12,
      width: 13,
    },
    '&:not(:only-child):last-child.focus-within::after': {
      transform: 'rotate(180deg)',
      transformOrigin: '12px 8px',
    }
  }
});

export enum SliderHandleIconType {
  Normal = 'normal',
  Left = 'left',
  Right = 'right'
}

export interface SliderHandleProps {
  ariaAttributes: Pick<React.AriaAttributes,
    'aria-label' | 'aria-labelledby' | 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow' | 'aria-valuetext'
  >;

  disabled?: boolean;

  /** @default SliderHandleIconType.normal */
  iconType?: SliderHandleIconType;

  isActive: boolean;
  isBeingTouched: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLElement>) => void;

  /* A proportion (0.0 to 1.0 inclusive) of where the center of the handle is horizontally inside its containing parent. */
  position: number;

  /** Test id to be attached to the slider and its contents. The suffix `'-SliderHandle'` will be appended.   */
  testId: string;
}

export interface SliderHandleState {
  isFocused: boolean;
  isHovered: boolean;
}

/**
 * This is just the handle part of the slider.
 *
 * It is interactive, BUT that logic (e.g. dragging) is handled by the container.  This allows some stuff like clicking
 * anywhere on the track to grab and drag the handle, rather than only being able to click on the handle.
 */
export class SliderHandleWidget extends React.Component<SliderHandleProps, SliderHandleState> {
  // We capture this ref so that we can 'focus()' it, and it is used indirectly for some hit testing logic in the
  // SliderHandleContainer.
  private imgRef: HTMLImageElement | null = null;
  // We capture this ref so that can give its client bounding box out to the SliderHandleContainer.  This area is
  // effectively the handle's touch target (or mouse target) area.
  private interactiveAreaRef: HTMLElement | null = null;

  public constructor(props: SliderHandleProps) {
    super(props);

    this.state = { isHovered: false, isFocused: false };
  }

  private chooseIconAndStylesForRender = (): {
    positioningContainerStyle: React.CSSProperties,
    pressIndicationBarStyle: React.CSSProperties,
    interactiveAreaStyle: React.CSSProperties,
    iconImageDataUrl: string,
    imgStyle: React.CSSProperties
  } => {
    const styles = getSliderHandleStyles();

    let iconSet = undefined;
    let interactiveAreaStyle = undefined;
    let imgStyle = undefined;

    switch (this.props.iconType) {
      case SliderHandleIconType.Left:
        iconSet = SliderLeftHandleIcon;
        interactiveAreaStyle = styles.HandleInteractiveAreaLeft;
        imgStyle = styles.HandleIconLeft;
        break;
      case SliderHandleIconType.Right:
        iconSet = SliderRightHandleIcon;
        interactiveAreaStyle = styles.HandleInteractiveAreaRight;
        imgStyle = styles.HandleIconRight;
        break;
      default:
        iconSet = SliderHandleIcon;
        interactiveAreaStyle = styles.HandleInteractiveArea;
        imgStyle = styles.HandleIcon;
        break;
    }

    if (this.props.disabled) {
      interactiveAreaStyle = { ...interactiveAreaStyle, ...styles.DisabledHandleMixin };
      imgStyle = { ...imgStyle, ...styles.DisabledHandleInnerFocusAreaMixin };
    }

    const iconState = {
      active: this.state.isHovered || this.props.isActive,
      disabled: this.props.disabled,
      focus: this.state.isFocused,
    };
    const iconImageDataUrl = iconSet.icon(iconState);

    return {
      positioningContainerStyle: styles.PositioningContainer,
      pressIndicationBarStyle: styles.PressIndicationBar,
      interactiveAreaStyle,
      imgStyle,
      iconImageDataUrl,
    };
  }

  private handleImgRef = (element: HTMLImageElement | null): void => {
    this.imgRef = element;
  }

  private handleInteractiveAreaRef = (element: HTMLElement | null): void => {
    this.interactiveAreaRef = element;
  }

  private handleFocus = (focus: boolean): void => {
    this.setState({ isFocused: !this.props.disabled && focus });
  }

  private handleHover = (hover: boolean): void => {
    if (!this.props.disabled) {
      this.setState({ isHovered: hover });
    }
  }

  public focus(): void {
    if (this.imgRef) {
      this.imgRef.focus();
    }
  }

  public getBoundingClientRectForIcon(): ClientRect {
    if (this.imgRef) {
      const rect = this.imgRef.getBoundingClientRect();
      // The left and right icons have several pixels of transparency between what appears like the edge of the artwork
      // and the actual edge of the image.  Unfortunately we are using the client bounding rect of the icon for some
      // behavior (e.g. see the dragStart method in SliderHandleContainer, which has logic that has to know whether
      // the user (with a mouse) clicked specifically on the icon or not).
      // To make the actual client bounding rect matchup visually with the edge of the artwork, we have to shrink
      // the bounding rect slightly.
      if (this.props.iconType === SliderHandleIconType.Left) {
        return addClientRectRight(rect, -1 * SliderLeftHandleIcon.artworkOffsetFromRightSide);
      } else if (this.props.iconType === SliderHandleIconType.Right) {
        return addClientRectLeft(rect, SliderRightHandleIcon.artworkOffsetFromLeftSide);
      }
      return rect;
    } else {
      throw new Error(`Can't determinte bounding rectangle of unmounted handle`);
    }
  }

  public getBoundingClientRectForInteractiveArea(): ClientRect {
    if (this.interactiveAreaRef) {
      return this.interactiveAreaRef.getBoundingClientRect();
    } else {
      throw new Error(`Can't determinte bounding rectangle of unmounted element`);
    }
  }

  public render(): JSX.Element {
    const dataTestId = makeTestId(this.props.testId, 'SliderHandle');
    const positionAsPercentage = this.props.position * 100;

    const { positioningContainerStyle, pressIndicationBarStyle, interactiveAreaStyle, imgStyle,
      iconImageDataUrl, } = this.chooseIconAndStylesForRender();

    const renderTouchDragIndicator: boolean = this.props.isActive && this.props.isBeingTouched;

    return (
      <InteractiveDomWrapper handleHover={this.handleHover} handleFocus={this.handleFocus}>
        <span
          className={TypeStyle.classes(FocusIndicatorStyleName, this.state.isFocused && 'focus-within')}
          data-tb-test-id={dataTestId}
          style={{ ...positioningContainerStyle, left: `${positionAsPercentage}%`, }}
        >
          {/* A vertical bar when the handle is 'pressed' (i.e. being dragged) */}
          {renderTouchDragIndicator &&
            <span style={pressIndicationBarStyle} data-tb-test-id={dataTestId + '-touch-drag-indicator'} />
          }
          {/* This is a wider area than the icon, making it easier to drag the icon by touch.*/}
          <span style={interactiveAreaStyle} ref={this.handleInteractiveAreaRef} />
          {/*The icon */}
          <img
            {...this.props.ariaAttributes}
            aria-orientation='horizontal'
            data-tb-test-id={makeTestId(this.props.testId, 'SliderHandleIconArea')}
            onKeyDown={this.props.onKeyDown}
            onKeyUp={this.props.onKeyUp}
            ref={this.handleImgRef}
            role='slider'
            src={iconImageDataUrl}
            style={imgStyle}
            tabIndex={this.props.disabled ? undefined : 0}
          />
        </span>
      </InteractiveDomWrapper>);
  }
}
